/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import { ColorName, Colors } from '../../../theme';
import type { Props as BadgeProps } from '../../display/Badge/Badge';
import { Icon } from '../../display/Icon';
import { Text } from '../../display/Text';
import { TooltipWrapper } from '../../display/TooltipWrapper';

import PageNavigationBadge from './PageNavigationBadge';
import PageNavigationRow from './PageNavigationRow';
import { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

const Styles = {
  // The width value for textContainer is 90% of the row width, less the
  // width of the space available for the Badge count (32px).
  textContainer: (showCount: boolean) => [
    css`
      align-items: center;
      display: flex;
      flex-direction: row;
      position: relative;
      width: calc(90% - var(--zds-size-32));
    `,
    showCount === false &&
      css`
        width: 100%;
      `,
  ],
  textEllipsis: (textColor: ColorName) => css`
    color: ${Colors[textColor]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  rowContainer: (_hover: boolean, _selected: boolean) => [
    css`
      border-radius: var(--zds-radius-small);
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: var(--zds-space-8) var(--zds-space-20);
    `,
  ],

  rowWrapper: (_hover: boolean, selected: boolean) => [
    selected &&
      css`
        border-radius: var(--zds-radius-small);
        background-color: ${Colors.UiPrimaryWeakest};
      `,
  ],
};

export default function NonNestedRow({
  item,
  onSelect,
  showCount,
}: {
  item: PageNavigationItem;
  onSelect?: OnSelectType;
  showCount: boolean;
}) {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const iconColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm6';
    const textColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm10';
    const badgeVariant = hover || selected ? 'primary-stronger' : 'neutral';
    const textType = selected ? 'Body3' : 'Body1';

    return (
      <div css={Styles.rowContainer(hover, selected)} data-zds>
        <span css={Styles.textContainer(showCount)} data-zds>
          {item.icon !== null && (
            <Icon color={iconColor} name={item.icon || 'navFolder'} size={24} />
          )}
          <span css={Styles.textEllipsis(textColor)} ref={textRef} data-zds>
            <Text color={textColor} margin="0 0 0 8px" type={textType}>
              {item.title}
            </Text>
          </span>
        </span>
        {item.count ? (
          <PageNavigationBadge variant={badgeVariant as BadgeProps['variant']}>
            {item.count.toString()}
          </PageNavigationBadge>
        ) : null}
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) => {
    const innerContent = isTruncated ? (
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={item.title}
        isBlock={true}
        position="northeast"
      >
        {() => <InnerContent hover={hover} selected={selected} />}
      </TooltipWrapper>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

    // Adding a wrapper here in order to properly set the background color in V5
    return (
      <div css={Styles.rowWrapper(hover, selected)} data-zds>
        {innerContent}
      </div>
    );
  };

  return (
    <PageNavigationRow content={Content} item={item} onSelect={onSelect} />
  );
}
